import { injectable } from 'tsyringe';
import { BaseViewModel } from '@/shared/viewmodels/BaseViewModel';
import {
    DirectoryApiService,
    type DirectoryUserRecord,
    type DirectoryEligibleConnection,
} from './DirectoryApiService';

const FETCH_PAGE_SIZE = 500;
const MAX_PAGES = 20;

@injectable()
export class DirectoryPageViewModel extends BaseViewModel {
    users: DirectoryUserRecord[] = [];
    connections: DirectoryEligibleConnection[] = [];

    loading: boolean = true;
    loadError: string | null = null;
    truncated: boolean = false;

    syncing: boolean = false;
    syncError: string | null = null;
    syncMessage: string | null = null;

    selectedUser: DirectoryUserRecord | null = null;
    isDrawerOpen: boolean = false;

    viewMode: 'grid' | 'org' = 'grid';

    searchTerm: string = '';
    statusFilter: string = 'all';
    departmentFilter: string = 'all';
    sourceFilter: string = 'all';

    constructor(private directory: DirectoryApiService) {
        super();
    }

    get availableDepartments(): string[] {
        return Array.from(new Set(this.users.map((u) => u.department).filter((d): d is string => !!d))).sort();
    }

    get filteredUsers(): DirectoryUserRecord[] {
        const q = this.searchTerm.trim().toLowerCase();
        return this.users.filter((u) => {
            if (this.statusFilter !== 'all' && u.status !== this.statusFilter) return false;
            if (this.departmentFilter !== 'all' && (u.department ?? '') !== this.departmentFilter) return false;
            if (this.sourceFilter !== 'all' && u.connectionId !== this.sourceFilter) return false;
            if (q && !`${u.displayName} ${u.email}`.toLowerCase().includes(q)) return false;
            return true;
        });
    }

    get hasActiveFilters(): boolean {
        return this.searchTerm !== '' || this.statusFilter !== 'all' || this.departmentFilter !== 'all' || this.sourceFilter !== 'all';
    }

    setSearchTerm(value: string): void {
        this.searchTerm = value;
        this.notifyChange();
    }

    setStatusFilter(value: string): void {
        this.statusFilter = value;
        this.notifyChange();
    }

    setDepartmentFilter(value: string): void {
        this.departmentFilter = value;
        this.notifyChange();
    }

    setSourceFilter(value: string): void {
        this.sourceFilter = value;
        this.notifyChange();
    }

    clearFilters(): void {
        this.searchTerm = '';
        this.statusFilter = 'all';
        this.departmentFilter = 'all';
        this.sourceFilter = 'all';
        this.notifyChange();
    }

    async initialize(): Promise<void> {
        await Promise.all([this.loadConnections(), this.loadUsers()]);
    }

    private async loadConnections(): Promise<void> {
        try {
            this.connections = await this.directory.listConnections();
        } catch {
            this.connections = [];
        }
        this.notifyChange();
    }

    private async loadUsers(): Promise<void> {
        this.loading = true;
        this.loadError = null;
        this.notifyChange();

        try {
            const collected: DirectoryUserRecord[] = [];
            let page = 1;
            let totalPages = 1;
            do {
                const result = await this.directory.listUsers({ page, pageSize: FETCH_PAGE_SIZE });
                collected.push(...result.items);
                totalPages = result.totalPages;
                page += 1;
            } while (page <= totalPages && page <= MAX_PAGES);
            this.users = collected;
            this.truncated = totalPages > MAX_PAGES;
        } catch (cause) {
            this.loadError = cause instanceof Error ? cause.message : 'Failed to load directory users';
            this.users = [];
        } finally {
            this.loading = false;
            this.notifyChange();
        }
    }

    async syncAll(): Promise<void> {
        this.syncing = true;
        this.syncError = null;
        this.syncMessage = null;
        this.notifyChange();
        try {
            const outcome = await this.directory.syncAll();
            const userCount = outcome.runs.reduce((sum, run) => sum + run.userCount, 0);
            const failed = outcome.runs.filter((run) => run.status === 'failed').length;
            this.syncMessage =
                failed > 0
                    ? `Synced ${userCount} users — ${failed} connection(s) failed.`
                    : `Synced ${userCount} users across ${outcome.runs.length} connection(s).`;
            await Promise.all([this.loadConnections(), this.loadUsers()]);
        } catch (cause) {
            this.syncError = cause instanceof Error ? cause.message : 'Directory sync failed';
        } finally {
            this.syncing = false;
            this.notifyChange();
        }
    }

    dismissSyncBanner(): void {
        this.syncMessage = null;
        this.syncError = null;
        this.notifyChange();
    }

    connectionLabel(connectionId: string): string {
        return this.connections.find((c) => c.id === connectionId)?.displayName ?? connectionId;
    }

    setViewMode(mode: 'grid' | 'org'): void {
        this.viewMode = mode;
        this.notifyChange();
    }

    get summary(): { total: number; active: number; inactive: number; sources: number } {
        const total = this.users.length;
        const active = this.users.filter((u) => u.status === 'active').length;
        return { total, active, inactive: total - active, sources: this.connections.length };
    }

    managerOf(user: DirectoryUserRecord): DirectoryUserRecord | null {
        if (!user.managerId) return null;
        return this.users.find((u) => u.connectionId === user.connectionId && u.externalId === user.managerId) ?? null;
    }

    reportsOf(user: DirectoryUserRecord): DirectoryUserRecord[] {
        return this.users.filter((u) => u.connectionId === user.connectionId && u.managerId === user.externalId);
    }

    openDrawer(user: DirectoryUserRecord): void {
        this.selectedUser = user;
        this.notifyChange();
        requestAnimationFrame(() => {
            this.isDrawerOpen = true;
            this.notifyChange();
        });
    }

    closeDrawer(): void {
        this.isDrawerOpen = false;
        this.notifyChange();
        setTimeout(() => {
            this.selectedUser = null;
            this.notifyChange();
        }, 250);
    }
}
